// RequestDocumentsDialog — staff picks exactly which documents to request from
// a client, then emails them a no-login upload link. This never reopens intake
// and never touches the client's existing data — it only collects files.
import { useState } from "react";
import { CheckCircle2, ClipboardCopy, Loader2, Send } from "lucide-react";
import { Modal } from "./Modal";
import { clients as clientsApi, REQUESTABLE_DOCS } from "../lib/api";

export function RequestDocumentsDialog({
  open,
  clientId,
  clientEmail,
  defaultKeys,
  onClose,
}: {
  open: boolean;
  clientId: string;
  clientEmail?: string | null;
  defaultKeys?: string[];
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    for (const k of defaultKeys || []) seed[k] = true;
    return seed;
  });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<{ link: string; delivered: boolean; error: string | null } | null>(null);

  const keys = Object.keys(picked).filter((k) => picked[k]);

  async function send() {
    setErr(null);
    if (!keys.length) { setErr("Pick at least one document."); return; }
    setBusy(true);
    try {
      const r = await clientsApi.requestDocuments(clientId, keys, note.trim() || undefined);
      setSent({ link: r.link, delivered: r.email_delivered, error: r.email_error });
    } catch (e: any) {
      setErr(e?.message || "Could not send the request.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request documents"
      footer={sent ? (
        <button className="primary-button" onClick={onClose}>Done</button>
      ) : (
        <>
          <button className="ghost-button" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary-button" onClick={send} disabled={busy}>
            {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} aria-hidden="true" />} Send upload link
          </button>
        </>
      )}
    >
      {sent ? (
        <div>
          <p style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CheckCircle2 size={18} style={{ color: "var(--ne-success, #2c8a3e)" }} aria-hidden="true" />
            {sent.delivered ? `Emailed to ${clientEmail || "the client"}.` : "Link created (email not sent — copy it below)."}
          </p>
          {!sent.delivered && sent.error && <p className="error-text">{sent.error}</p>}
          <div className="intake-url-row" style={{ marginTop: 10 }}>
            <code>{sent.link}</code>
            <button className="icon-button" title="Copy link" onClick={() => navigator.clipboard.writeText(sent.link)}>
              <ClipboardCopy size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="muted small" style={{ marginBottom: 10 }}>
            Emails a no-login upload link for only what you check. It never reopens intake or changes anything already on file.
          </p>
          {err && <p className="error-text">{err}</p>}
          <div className="stacked-form" style={{ gap: 4 }}>
            {REQUESTABLE_DOCS.map((d) => (
              <label key={d.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 2px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!picked[d.key]}
                  onChange={(e) => setPicked((p) => ({ ...p, [d.key]: e.target.checked }))}
                />
                <span>{d.label}</span>
              </label>
            ))}
          </div>
          <label className="form-field" style={{ marginTop: 10 }}>
            <span>Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Front and back, please" />
          </label>
          {!clientEmail && <p className="muted small" style={{ marginTop: 6 }}>This client has no email on file — add one first, or you'll only get a copyable link.</p>}
        </>
      )}
    </Modal>
  );
}
